import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
  CSelect,
  CAlert,
  CSpinner,
} from "@coreui/react";
import { RootState } from "../../store";
import { createItem, getItem } from "../../api/generics";
import { SUCCESS } from "../../utils/constants/tags";
import CompanyAdditionalFields from "../../models/company-additional-fields";

const FIELD_TYPES = [
  { value: "", label: "Seleccione tipo" },
  { value: "text", label: "Texto" },
  { value: "number", label: "Número" },
  { value: "date", label: "Fecha" },
  { value: "select", label: "Selección" },
  { value: "checkbox", label: "Casilla de verificación" },
  { value: "textarea", label: "Área de texto" },
];

const CompanyAdditionalFieldsForm: React.FC = () => {
  const companyId = useSelector(
    (state: RootState) => state.company.data.company.id
  );

  const [formData, setFormData] = useState<CompanyAdditionalFields>({
    companyId: companyId,
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [alert, setAlert] = useState<{
    type: "success" | "danger" | "info";
    message: string;
  } | null>(null);

  const [choicesInput, setChoicesInput] = useState<{ [key: number]: string }>(
    {}
  );

  useEffect(() => {
    loadAdditionalFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const loadAdditionalFields = async () => {
    try {
      setLoading(true);
      const response = await getItem<CompanyAdditionalFields>(
        `/company_additional_fields/${companyId}/`
      );

      if (response.status === SUCCESS && response.data) {
        setFormData({ ...response.data, companyId: companyId });

        const initialChoices: { [key: number]: string } = {};
        for (let i = 1; i <= 10; i++) {
          const choices = response.data[`fieldChoices${i}`];
          if (choices && Array.isArray(choices)) {
            initialChoices[i] = choices.join(", ");
          }
        }
        setChoicesInput(initialChoices);
      }
    } catch (error) {
      console.error("Error loading additional fields:", error);
      setAlert({
        type: "danger",
        message: "Error al cargar los campos adicionales",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (
    fieldIndex: number,
    fieldType: "name" | "type",
    value: string
  ) => {
    const fieldKey =
      fieldType === "name"
        ? `fieldName${fieldIndex}`
        : `fieldType${fieldIndex}`;
    setFormData((prev) => ({
      ...prev,
      [fieldKey]: value,
    }));
  };

  const handleChoicesChange = (fieldIndex: number, value: string) => {
    setChoicesInput((prev) => ({
      ...prev,
      [fieldIndex]: value,
    }));

    const choicesArray = value
      .split(",")
      .map((choice) => choice.trim())
      .filter((choice) => choice.length > 0);

    setFormData((prev) => ({
      ...prev,
      [`fieldChoices${fieldIndex}`]:
        choicesArray.length > 0 ? choicesArray : undefined,
    }));
  };

  const clearField = (fieldIndex: number) => {
    setFormData((prev) => ({
      ...prev,
      [`fieldName${fieldIndex}`]: undefined,
      [`fieldType${fieldIndex}`]: undefined,
      [`fieldChoices${fieldIndex}`]: undefined,
    }));
    setChoicesInput((prev) => {
      const newChoices = { ...prev };
      delete newChoices[fieldIndex];
      return newChoices;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setSaving(true);
      setAlert(null);

      // use generic createItem helper instead of direct fetch
      const result = await createItem(
        "/company_additional_fields/create_or_update/",
        formData
      );

      if (result && result.status === "success") {
        setAlert({
          type: "success",
          message: "Campos adicionales guardados exitosamente",
        });
        // reload to reflect saved values but remain in the form
        await loadAdditionalFields();
      } else {
        throw new Error(
          result?.detail || "Error al guardar los campos adicionales"
        );
      }
    } catch (error) {
      console.error("Error saving additional fields:", error);
      setAlert({
        type: "danger",
        message:
          error instanceof Error
            ? error.message
            : "Error al guardar los campos adicionales",
      });
    } finally {
      setSaving(false);
    }
  };

  const renderFieldGroup = (fieldIndex: number) => {
    const fieldName = formData[
      `fieldName${fieldIndex}` as keyof CompanyAdditionalFields
    ] as string | undefined;
    const fieldType = formData[
      `fieldType${fieldIndex}` as keyof CompanyAdditionalFields
    ] as string | undefined;
    const showChoices = fieldType === "select";

    return (
      <CCard key={fieldIndex} className="mb-3">
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <strong>Campo Adicional {fieldIndex}</strong>
          <CButton
            color="danger"
            size="sm"
            onClick={() => clearField(fieldIndex)}
            disabled={!fieldName && !fieldType}
          >
            <i className="fa-solid fa-trash"></i> Limpiar
          </CButton>
        </CCardHeader>
        <CCardBody>
          <CRow>
            <CCol md="6">
              <CFormGroup>
                <CLabel htmlFor={`fieldName${fieldIndex}`}>
                  Nombre del Campo
                </CLabel>
                <CInput
                  id={`fieldName${fieldIndex}`}
                  placeholder="Ej: Número de Empleado"
                  value={fieldName || ""}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleFieldChange(fieldIndex, "name", e.target.value)
                  }
                />
              </CFormGroup>
            </CCol>
            <CCol md="6">
              <CFormGroup>
                <CLabel htmlFor={`fieldType${fieldIndex}`}>
                  Tipo de Campo
                </CLabel>
                <CSelect
                  id={`fieldType${fieldIndex}`}
                  value={fieldType || ""}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    handleFieldChange(fieldIndex, "type", e.target.value)
                  }
                >
                  {FIELD_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </CSelect>
              </CFormGroup>
            </CCol>
          </CRow>

          {showChoices && (
            <CRow>
              <CCol md="12">
                <CFormGroup>
                  <CLabel htmlFor={`fieldChoices${fieldIndex}`}>
                    Opciones (separadas por comas)
                  </CLabel>
                  <CInput
                    id={`fieldChoices${fieldIndex}`}
                    placeholder="Ej: Opción 1, Opción 2, Opción 3"
                    value={choicesInput[fieldIndex] || ""}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChoicesChange(fieldIndex, e.target.value)
                    }
                  />
                  <small className="form-text text-muted">
                    Ingrese las opciones separadas por comas
                  </small>
                </CFormGroup>
              </CCol>
            </CRow>
          )}
        </CCardBody>
      </CCard>
    );
  };

  if (loading) {
    return (
      <CCard>
        <CCardBody className="text-center">
          <CSpinner color="primary" />
          <p className="mt-2">Cargando campos adicionales...</p>
        </CCardBody>
      </CCard>
    );
  }

  return (
    <CForm onSubmit={handleSubmit}>
      <CCard>
        <CCardHeader>
          <strong>Configuración de Campos Adicionales</strong>
        </CCardHeader>
        <CCardBody>
          {alert && (
            <CAlert color={alert.type} closeButton>
              {alert.message}
            </CAlert>
          )}

          <p className="text-muted mb-4">
            Configure hasta 10 campos adicionales personalizados para su
            empresa. Los campos de tipo "Selección" requieren opciones separadas
            por comas.
          </p>

          {Array.from({ length: 10 }, (_, i) => i + 1).map((fieldIndex) =>
            renderFieldGroup(fieldIndex)
          )}

          <div className="d-flex justify-content-end mt-4">
            <CButton type="submit" color="primary" disabled={saving}>
              {saving ? (
                <>
                  <CSpinner size="sm" className="mr-2" />
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-floppy-disk mr-2"></i>
                  Guardar Configuración
                </>
              )}
            </CButton>
          </div>
        </CCardBody>
      </CCard>
    </CForm>
  );
};

export default CompanyAdditionalFieldsForm;
